import type { ReactNode } from "react";
import { z } from "zod";

import type { ScreeningAnswerCreateModel } from "@av/apps/public/jobs/services/apply/types";
import type { ERROR_CODE_I18N_MAPPING } from "@av/apps/recruitment/ats-common/services/api-config/errors/error-i18n-mappings";
import type { SupportedLocales } from "@av/components/organisms/editor/localized-text-editor/text-editor-supported-locales";
import type { Dayjs } from "@av/config/extended-dayjs";
import type { LookupEntity } from "@av/config/types/entities/Entities";
import type { Province } from "@av/types/province";

import type { ScreeningAnswerViewModel, ScreeningQuestion } from "./screening-question/types";
import type { AxiosError } from "axios";

export { AllProvinces, type Province } from "@av/types/province";

export type ProvinceEntity = LookupEntity<Province>;
export type LocationTypeEntity = LookupEntity<LocationType>;
export type EmploymentTypeEntity = LookupEntity<EmploymentType>;

export type PostedTo = "INTERNAL" | "EXTERNAL" | "JOB_BOARD";
export type JobPostStatus = "DRAFT" | "POSTED" | "ARCHIVED" | "CLOSED";
export type LocationType = "REMOTE" | "HYBRID" | "IN_PERSON";
export const AllLocationTypes: LocationType[] = ["REMOTE", "HYBRID", "IN_PERSON"];
export type EmploymentType = "FULL_TIME" | "PART_TIME" | "CO_OP" | "FIXED_TERM";
export type PayType = "SALARY" | "HOURLY";
export type PayDisplay = "RANGE" | "AMOUNT";
export type CurrencyCode = "CAD";
export const DEFAULT_CURRENCY_CODE: CurrencyCode = "CAD";
export type HiringTeamUser = {
  userId: string;
  displayName: string | null;
};
export type HiringTeamUsers = {
  recruiters: HiringTeamUser[];
  hiringManagers: HiringTeamUser[];
};
export const locationTypeValues: LocationType[] = ["REMOTE", "HYBRID", "IN_PERSON"];

export interface PublicJobPostModel {
  jobPostId: string;
  jobId: number;
  jobTitles: Record<string, string>;
  descriptions: Record<string, string>;
  isResumeRequired: boolean;
  isCoverLetterRequired?: boolean;
  isSupportingDocumentsRequired?: boolean;

  payType: PayType;
  payDisplay?: PayDisplay;
  minCompensation?: number;
  maxCompensation?: number;
  currencyCode?: CurrencyCode;
  includeCompensationOnJobPost: boolean;
}

export interface JobPostModel {
  jobPostId: string;
  companyId: string;
  jobId: number;
  jobTitles: Record<string, string>;
  descriptions: Record<string, string>;
  isResumeRequired: boolean;
  isCoverLetterRequired?: boolean;
  postedDate: Dayjs;
  closedDate?: Dayjs;
  archivedDate?: Dayjs;
  hiredCandidateNames?: string[];
  recruiters?: HiringTeamUser[];
  hiringManagers?: HiringTeamUser[];

  isPostedInternally: boolean;
  isPostedExternally: boolean;
  isPostedOnJobBoard: boolean;

  postStatus: JobPostStatus;

  candidateCount: number;
  filledPositions: number;
  nonTerminalCandidateCount: number;

  // Optional during the province rollout; the list API returns null for posts with no province set
  subdivisionCode?: Province | null;
  city?: string | null;
}

export interface JobPostCreateModel
  extends Omit<
    JobPostModel,
    | "candidateCount"
    | "closedDate"
    | "filledPositions"
    | "nonTerminalCandidateCount"
    | "recruiters"
    | "hiringManagers"
  > {
  recruiters?: string[];
  hiringManagers?: string[];
  payType: PayType;
  payDisplay?: PayDisplay;
  minCompensation?: number;
  maxCompensation?: number;
  currencyCode?: CurrencyCode;
  includeCompensationOnJobPost: boolean;
  // Temporarily nullable for independent deployments
  includeApplyByOnJobPost?: boolean;
}

export interface MockJobPostData
  extends Omit<
    JobPostModel,
    | "postedDate"
    | "closedDate"
    | "archivedDate"
    | "minCompensation"
    | "maxCompensation"
    | "currencyCode"
    | "isPostedInternally"
    | "isPostedExternally"
    | "isPostedOnJobBoard"
    | "recruiters"
    | "hiringManagers"
  > {
  closedDate?: string;
  archivedDate?: string;
  hiredCandidateNames?: string[];
  locationType: LocationType;
  employmentType: EmploymentType;
  subdivisionCode: Province;
  jobCountryCode: string;
  city: string;
  streetAddress?: string;
  postedDate: string;
  applyBy?: string;

  totalPositions?: number;
  pipeline?: number;

  isPostedInternally?: boolean;
  isPostedExternally?: boolean;
  isPostedOnJobBoard?: boolean;

  payType: PayType;
  payDisplay?: PayDisplay;
  minCompensation?: number;
  maxCompensation?: number;
  currencyCode?: CurrencyCode;
  includeCompensationOnJobPost: boolean;
  includeApplyByOnJobPost?: boolean;
  isSupportingDocumentsRequired?: boolean;

  recruiters?: HiringTeamUser[];
  hiringManagers?: HiringTeamUser[];
  applicationIds?: string[];
}

export interface JobPostStats {
  filledPositions?: number;
  totalPositions?: number;
  candidateCount?: number;
  pipeline?: number;
}

export type JobPostFilters = {
  startDate?: Dayjs;
  endDate?: Dayjs;
  statuses?: JobPostStatus[];
  excludedStatuses?: JobPostStatus[];
  jobPostId?: string;
  searchText?: string;
  city?: string[];
  subdivisionCodes?: string[];
  locationType?: LocationType;
  compensation?: number;
  page?: number;
};

export type JobPostDataUI = Omit<
  MockJobPostData & JobPostStats,
  | "postedDate"
  | "closedDate"
  | "archivedDate"
  | "applyBy"
  | "recruiters"
  | "hiringManagers"
  | "subdivisionCode"
> & {
  postedDate?: Dayjs;
  closedDate?: Dayjs;
  archivedDate?: Dayjs;
  applyBy?: Dayjs;
  recruiters?: HiringTeamUser[];
  hiringManagers?: HiringTeamUser[];
  // List posts can arrive with no province set (the list API returns null; remote jobs have none)
  subdivisionCode: Province | null;
};

export type JobPostCreateDataUI = Omit<
  JobPostDataUI,
  | "recruiters"
  | "hiringManagers"
  | "postedDate"
  | "closedDate"
  | "archivedDate"
  | "hiredCandidateNames"
> & {
  city?: string;
  subdivisionCode?: Province;
  jobCountryCode?: string;
  selectedLocales: SupportedLocales[];
  recruiters?: string[];
  hiringManagers?: string[];
  screeningQuestions?: JobPostScreeningQuestionModel[];
  isSupportingDocumentsRequired?: boolean;
  payDisplay?: PayDisplay;
  payAmount?: number;
};

export type JobPostEditDataUI = Omit<JobPostCreateDataUI, "jobPostId" | "postStatus"> & {
  applyBy?: Dayjs;
  version: number;
};

export interface JobPostDetailsModel {
  jobPostId: string;
  companyId: string;
  jobId: number;
  jobTitles: Record<string, string>;

  descriptions: Record<SupportedLocales, string>;
  city?: string;
  streetAddress?: string;
  countryCode?: string;
  subdivisionCode?: Province;
  employmentType?: EmploymentType;
  locationType?: LocationType;
  postStatus?: JobPostStatus;
  payType?: PayType;
  payDisplay?: PayDisplay;

  minCompensation?: number;
  maxCompensation?: number;
  currencyCode?: CurrencyCode;
  includeCompensationOnJobPost: boolean;
  includeApplyByOnJobPost?: boolean;

  recruiters?: HiringTeamUser[];
  hiringManagers?: HiringTeamUser[];

  isPostedInternally?: boolean;
  isPostedExternally?: boolean;
  isPostedOnJobBoard?: boolean;
  isResumeRequired?: boolean;
  isCoverLetterRequired?: boolean;
  isSupportingDocumentsRequired?: boolean;

  postedDate?: string;
  closedDate?: string;
  archivedDate?: string;
  hiredCandidateNames?: string[];
  applyBy?: string;

  candidateCount: number;
  filledPositions: number;

  screeningQuestions: JobPostScreeningQuestion[];

  /**
   * Optimistic-lock counter set by the server. Echoed back on edit so the
   * server can detect concurrent edits via CAS. New job posts ship at 0.
   */
  version: number;
}

export type JobPostDetailsUI = Omit<
  JobPostDetailsModel,
  "postedDate" | "closedDate" | "archivedDate" | "applyBy"
> & {
  postedDate?: Dayjs;
  closedDate?: Dayjs;
  archivedDate?: Dayjs;
  applyBy?: Dayjs;
  recruiterNames: string;
  hiringManagerNames: string;
};

export interface PublicJobPostDetailsModel {
  jobPostId: string;
  companyId: string;
  jobId: number;
  jobTitles: Record<string, string>;

  descriptions: Record<SupportedLocales, string>;
  city?: string;
  streetAddress?: string;
  countryCode?: string;
  subdivisionCode?: Province;
  employmentType?: EmploymentType;
  locationType?: LocationType;
  postStatus?: JobPostStatus;
  payType?: PayType;
  payDisplay?: PayDisplay;

  minCompensation?: number;
  maxCompensation?: number;
  currencyCode?: CurrencyCode;
  includeCompensationOnJobPost: boolean;

  isResumeRequired?: boolean;
  isCoverLetterRequired?: boolean;
  isSupportingDocumentsRequired?: boolean;

  postedDate?: Dayjs;
  applyBy?: Dayjs;

  screeningQuestions: JobPostScreeningQuestion[];
}

export type JobPostScreeningQuestionModel = {
  screeningQuestionId: string;
  version: number;
  idealBooleanAnswer: boolean;
  questions: ScreeningQuestion;
  isActiveOnJobPost: boolean;
};

export type JobPostScreeningQuestion = JobPostScreeningQuestionModel & {
  jobPostId: string;
  companyId: string;
  answerCount?: number;
};

export type IconEntity = {
  label: (color: string) => ReactNode;
  value: string;
};

export type ApiErrorCode = keyof typeof ERROR_CODE_I18N_MAPPING;
export interface ApiValidationError {
  field: string;
  code: ApiErrorCode;
  message: string;
}

export const ValidationErrorSchema = z.object({
  field: z.string(),
  code: z.string() as z.ZodType<ApiErrorCode>,
  message: z.string(),
  params: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]))
    .optional(),
});

export const ApiErrorResponseSchema = z.object({
  correlationId: z.string(),
  error: z.object({ code: z.string(), message: z.string() }),
  status: z.string(),
  statusCode: z.number(),
  timestamp: z.string(),
  validationErrors: z.array(ValidationErrorSchema),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
export type ValidationError = z.infer<typeof ValidationErrorSchema>;

export type ApiError = AxiosError<ApiErrorResponse>;

export type ApplicationDocumentType =
  | "RESUME"
  | "COVER_LETTER"
  | "SUPPORTING_DOCUMENT"
  | "SCREENING_AND_INTERVIEW_DOCUMENT"
  | "OFFER_AND_HIRING_DOCUMENT";

export type ApplicationModel = {
  applicationId: string;
  candidate: CandidateModel;
  documents: DocumentCreateModel[];
  screeningAnswers?: ScreeningAnswerCreateModel[];
  dataSharingConsentGiven?: boolean;
};

export type ApplicationViewModel = {
  applicationId: string;
  candidate: CandidateModel;
  documents: DocumentCreateModel[];
  hasIndeedJsonResume?: boolean;
  screeningAnswers?: ScreeningAnswerViewModel[];
  isCoverLetterRequired?: boolean;
  isSupportingDocumentsRequired?: boolean;
};

export type CandidateModel = {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email: string;
  phoneNumber: string;
  linkedIn?: string;
  country: string;
  subdivision: string;
  city: string;
};

export type ApplicationCandidateUI = CandidateModel & {
  formattedPhone: string;
  address: string;
};

export type ApplicationUI = Omit<ApplicationViewModel, "candidate"> & {
  candidate: ApplicationCandidateUI;
};

export type DocumentCreateModel = {
  applicationDocumentId: string;
  documentType: ApplicationDocumentType;
  creatorType: "CANDIDATE";
  name: string;
  filename: string;
  contentType: string;
  fileSizeBytes: number;
};

export type DocumentCreateResponse = {
  applicationDocumentId: string;
  presignedPostUrl: string;
  presignedPostFields: Record<string, string>;
};
